import { TECHNOLOGY_TAB_STICKY_CLASS } from '@components/pages/technology/components/shared/technology-container-query.constants';
import { StickyColumn } from '@components/shared/sticky-column.component';
import { STICKY_CONTAINER_STYLE } from '@lib/sticky.utils';
import { cn } from '@lib/utils';
import { FC, ReactNode } from 'react';

type TechnologyTabLayoutProps = {
  className?: string;
  contextColumn?: ReactNode;
  isTransitioning?: boolean;
  mainContent: ReactNode;
  sidebar?: ReactNode;
  sidebarClassName?: string;
  variant: 'content-content' | 'content-context' | 'sidebar-content' | 'sidebar-content-context';
};

/**
 * Shared layout component for technology page tabs.
 *
 * Variants:
 * - `content-content`: Two content columns (Details tab) - left column is smaller content
 * - `content-context`: Main content + right context column (MA create/edit pages)
 * - `sidebar-content`: Sidebar navigation + main content (TRL/MRL tabs)
 * - `sidebar-content-context`: Sidebar + main content + right context column (MA tab)
 *
 * Column widths snap at breakpoints:
 * - Sidebar/Context: w-[280px] below 2xl (1440px), w-80 (320px) at 2xl+
 */
export const TechnologyTabLayout: FC<TechnologyTabLayoutProps> = (props) => {
  const { className, contextColumn, isTransitioning, mainContent, sidebar, sidebarClassName, variant } = props;

  const sidebarClasses = cn(
    'shrink-0',
    variant === 'content-content' ? 'w-80' : 'w-[280px] 2xl:w-80',
    sidebarClassName,
  );

  const contextClasses = 'w-[280px] 2xl:w-80 shrink-0';
  const hasContextColumn = variant === 'sidebar-content-context' || variant === 'content-context';
  const isStickyLayout = variant === 'sidebar-content-context';
  const stickyClassName = isStickyLayout ? TECHNOLOGY_TAB_STICKY_CLASS : undefined;

  return (
    <div
      className={cn(
        'flex gap-4 items-start',
        isTransitioning && 'opacity-60 pointer-events-none',
        'transition-opacity duration-200',
        className,
      )}
      style={STICKY_CONTAINER_STYLE}
    >
      {/* Sidebar / Left Column */}
      {sidebar && (
        <StickyColumn
          className={cn(sidebarClasses, stickyClassName)}
          isSticky={isStickyLayout}
        >
          {sidebar}
        </StickyColumn>
      )}

      {/* Main Content - also sticky so shorter content sticks when taller sidebar scrolls */}
      <StickyColumn
        className={cn('flex-1 min-w-0', stickyClassName)}
        isSticky={isStickyLayout}
      >
        {mainContent}
      </StickyColumn>

      {/* Context Column (optional, for MA tab and MA create/edit pages) */}
      {hasContextColumn && contextColumn && (
        <StickyColumn
          className={cn(contextClasses, stickyClassName)}
          isSticky={isStickyLayout}
        >
          {contextColumn}
        </StickyColumn>
      )}
    </div>
  );
};
